import { useEffect, useState } from "react";
import api from "@/api";
import { isMuted } from "@/utils/sound";

export default function RetrospectiveBanner() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(isMuted());

  useEffect(() => {
    api.get("/retro").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const onSound = (e) => setMuted(e.detail);
    window.addEventListener("cetf:sound", onSound);
    return () => window.removeEventListener("cetf:sound", onSound);
  }, []);

  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 4000);
    return () => clearInterval(t);
  }, [items.length]);

  if (items.length === 0) return null;

  const current = idx % items.length;
  const item = items[current];
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/retro-media/${item.storage_path}`;

  return (
    <section className="bg-slate-900 text-white py-10" data-testid="retro-banner">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-4">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-amber-400">Nos événements passés</p>
            <h2 className="text-2xl font-bold tracking-tight" data-testid="retro-banner-title">Rétrospective &amp; Pub</h2>
          </div>
          {items.length > 1 && (
            <div className="flex gap-1.5" data-testid="retro-banner-dots">
              {items.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIdx(i)}
                  aria-label={`Aller à la diapositive ${i + 1}`}
                  className={`h-2 rounded-full transition-all ${i === current ? "w-6 bg-amber-400" : "w-2 bg-white/30"}`}
                />
              ))}
            </div>
          )}
        </div>
        <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-black/40 shadow-2xl" data-testid="retro-banner-slide">
          {item.media_type === "video" ? (
            <video key={item.id} src={url} autoPlay loop muted={muted} playsInline className="w-full h-64 sm:h-80 object-cover" />
          ) : item.media_type === "image" ? (
            <img key={item.id} src={url} alt={item.title} className="w-full h-64 sm:h-80 object-cover" loading="lazy" />
          ) : (
            <a href={url} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center h-64 sm:h-80 gap-3 hover:bg-white/5 transition-colors">
              <span className="text-[10px] font-bold uppercase tracking-widest bg-amber-400 text-slate-900 rounded-full px-3 py-1.5">Document PDF</span>
              <span className="text-sm font-medium text-white/80 px-6 text-center">{item.title || "Voir le document"}</span>
            </a>
          )}
          {(item.title || item.link) && (
            <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent px-5 py-4 flex items-end justify-between gap-4">
              <p className="text-sm sm:text-base font-semibold" data-testid="retro-banner-caption">{item.title}</p>
              {item.link && (
                <a href={item.link} target="_blank" rel="noopener noreferrer"
                  className="text-xs font-semibold bg-amber-400 text-slate-900 rounded-full px-4 py-2 hover:bg-amber-300 transition-colors shrink-0"
                  data-testid="retro-banner-link">
                  En savoir plus
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
